/** @format */

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Typography,
    Chip,
    TextField,
    MenuItem,
    CircularProgress,
    Alert,
    Divider,
    Button,
    FormControlLabel,
    Checkbox,
    Stack,
} from "@mui/material";
import {
    ArrowBackOutlined,
    SendOutlined,
    CheckCircleOutline,
    NoteAltOutlined,
} from "@mui/icons-material";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { selectIsPlatformSuperAdmin } from "../../../redux/selectors/collectSelector";
import {
    fetchAdminSupportTicket,
    updateAdminSupportTicket,
    addAdminSupportMessage,
    resolveAdminSupportTicket,
    clearCurrentTicket,
} from "../../../redux/slices/collectSupportSlice";
import {
    SupportTicketStatus,
    SupportTicketPriority,
} from "../../../utility/types/collectTypes";
import {
    CollectPageWrapper,
    CollectHeroBanner,
    CollectHeroInner,
    CollectBadge,
    CollectHeroTitle,
    CollectContentArea,
    CollectSectionCard,
    CollectSectionTitle,
    CollectSectionSubtitle,
    CollectPrimaryButton,
    CollectSecondaryButton,
} from "../styles";
import {
    SUPPORT_PRIORITY_OPTIONS,
    supportStatusChipSx,
    supportStatusLabel,
    supportTierChipSx,
    subscriptionTierChipSx,
    supportCategoryLabel,
    formatDateTime,
} from "../supportShared";

// Mirror of the backend status state machine (defense-in-depth; server is
// authoritative). Used to only offer valid next statuses.
const ALLOWED_TRANSITIONS: Record<string, SupportTicketStatus[]> = {
    open: ["in_progress", "waiting_on_user", "resolved", "closed"],
    in_progress: ["waiting_on_user", "resolved", "closed"],
    waiting_on_user: ["in_progress", "resolved", "closed"],
    resolved: ["closed", "reopened"],
    closed: ["reopened"],
    reopened: ["in_progress", "waiting_on_user", "resolved", "closed"],
};

const CollectSupportTicketAdmin: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { ticketId } = useParams<{ ticketId: string }>();
    const isPlatformSuperAdmin = useSelector(selectIsPlatformSuperAdmin);
    const myBusinessUserId = useSelector(
        (s: RootState) => s.businessAuth.businessUser?._id,
    );
    const { adminCurrentTicket, isLoading, isSubmitting, error, submitError } =
        useSelector((s: RootState) => s.collectSupport);

    const [reply, setReply] = useState("");
    const [isInternalNote, setIsInternalNote] = useState(false);
    const [resolveNote, setResolveNote] = useState("");
    const [nextStatus, setNextStatus] = useState<string>("");

    const load = useCallback(() => {
        if (ticketId) dispatch(fetchAdminSupportTicket(ticketId));
    }, [ticketId, dispatch]);

    useEffect(() => {
        load();
        return () => {
            dispatch(clearCurrentTicket());
        };
    }, [load, dispatch]);

    const ticket = adminCurrentTicket?.ticket;
    const messages = adminCurrentTicket?.messages || [];
    const liveSub = adminCurrentTicket?.liveSubscription;
    const business = adminCurrentTicket?.business;
    const assignee = adminCurrentTicket?.assignee;

    const allowedNext = ticket
        ? ALLOWED_TRANSITIONS[ticket.status] || []
        : [];

    const handleStatusChange = async () => {
        if (!ticketId || !nextStatus) return;
        const result = await dispatch(
            updateAdminSupportTicket({
                ticketId,
                payload: { status: nextStatus as SupportTicketStatus },
            }),
        );
        if (updateAdminSupportTicket.fulfilled.match(result)) {
            setNextStatus("");
            load();
        }
    };

    const handlePriorityChange = async (priority: SupportTicketPriority) => {
        if (!ticketId) return;
        await dispatch(
            updateAdminSupportTicket({ ticketId, payload: { priority } }),
        );
        load();
    };

    const handleAssign = async (assignToMe: boolean) => {
        if (!ticketId) return;
        await dispatch(
            updateAdminSupportTicket({
                ticketId,
                payload: {
                    assignedToBusinessUserId: assignToMe
                        ? myBusinessUserId || null
                        : null,
                },
            }),
        );
        load();
    };

    const handleSendMessage = async () => {
        if (!ticketId || !reply.trim()) return;
        const result = await dispatch(
            addAdminSupportMessage({
                ticketId,
                body: reply.trim(),
                isInternalNote,
            }),
        );
        if (addAdminSupportMessage.fulfilled.match(result)) {
            setReply("");
            setIsInternalNote(false);
        }
    };

    const handleResolve = async () => {
        if (!ticketId) return;
        const result = await dispatch(
            resolveAdminSupportTicket({
                ticketId,
                note: resolveNote.trim() || undefined,
            }),
        );
        if (resolveAdminSupportTicket.fulfilled.match(result)) {
            setResolveNote("");
            load();
        }
    };

    return (
        <CollectPageWrapper>
            <CollectHeroBanner>
                <CollectHeroInner>
                    <Button
                        startIcon={<ArrowBackOutlined />}
                        onClick={() => navigate("/collect/admin/support")}
                        sx={{
                            color: "rgba(255,255,255,0.8)",
                            textTransform: "none",
                            alignSelf: "flex-start",
                            px: 0,
                        }}
                    >
                        Back to Incident Queue
                    </Button>
                    <CollectBadge label="Super Admin" />
                    <CollectHeroTitle>
                        {ticket ? ticket.ticketNumber : "Ticket"}
                    </CollectHeroTitle>
                    {ticket && (
                        <Typography sx={{ color: "rgba(255,255,255,0.85)" }}>
                            {ticket.subject}
                        </Typography>
                    )}
                </CollectHeroInner>
            </CollectHeroBanner>

            <CollectContentArea>
                {!isPlatformSuperAdmin && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This area is restricted to platform super admins.
                    </Alert>
                )}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {isLoading && !ticket ? (
                    <Box sx={{ textAlign: "center", py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : !ticket ? (
                    <CollectSectionCard>
                        <Typography>Ticket not found.</Typography>
                    </CollectSectionCard>
                ) : (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
                            gap: 3,
                        }}
                    >
                        {/* ── Left: conversation + reply ── */}
                        <Box>
                            <CollectSectionCard>
                                <CollectSectionTitle>
                                    Conversation
                                </CollectSectionTitle>
                                <CollectSectionSubtitle>
                                    {supportCategoryLabel(ticket.category)} ·
                                    user priority {ticket.priority}
                                </CollectSectionSubtitle>

                                <Box sx={{ mt: 2 }}>
                                    <AdminMessage
                                        author={
                                            ticket.submitterName ||
                                            ticket.submitterEmail
                                        }
                                        body={ticket.description}
                                        at={ticket.createdAt}
                                        kind="user"
                                    />
                                    {messages.map((m) => (
                                        <AdminMessage
                                            key={m._id}
                                            author={
                                                m.authorType === "agent"
                                                    ? m.authorName || "Support"
                                                    : m.authorName ||
                                                    ticket.submitterEmail
                                            }
                                            body={m.body}
                                            at={m.createdAt}
                                            kind={
                                                m.isInternalNote
                                                    ? "internal"
                                                    : m.authorType === "agent"
                                                        ? "agent"
                                                        : "user"
                                            }
                                        />
                                    ))}
                                    {ticket.resolutionNote && (
                                        <Alert severity="success" sx={{ mt: 2 }}>
                                            <strong>Resolution:</strong>{" "}
                                            {ticket.resolutionNote}
                                        </Alert>
                                    )}
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                {submitError && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {submitError}
                                    </Alert>
                                )}

                                <TextField
                                    label={
                                        isInternalNote
                                            ? "Internal note (not sent to user)"
                                            : "Reply to user"
                                    }
                                    value={reply}
                                    onChange={(e) =>
                                        setReply(e.target.value.slice(0, 10000))
                                    }
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    size="small"
                                    sx={{ mb: 1 }}
                                />
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        flexWrap: "wrap",
                                        gap: 1,
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={isInternalNote}
                                                onChange={(e) =>
                                                    setIsInternalNote(
                                                        e.target.checked,
                                                    )
                                                }
                                                size="small"
                                            />
                                        }
                                        label={
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                }}
                                            >
                                                <NoteAltOutlined
                                                    sx={{ fontSize: 16 }}
                                                />
                                                <span>Internal note</span>
                                            </Box>
                                        }
                                    />
                                    <CollectPrimaryButton
                                        startIcon={<SendOutlined />}
                                        onClick={handleSendMessage}
                                        disabled={isSubmitting || !reply.trim()}
                                    >
                                        {isInternalNote
                                            ? "Add note"
                                            : "Send reply"}
                                    </CollectPrimaryButton>
                                </Box>
                            </CollectSectionCard>
                        </Box>

                        {/* ── Right: triage panel ── */}
                        <Box>
                            {/* Subscription tier + support priority — prominent */}
                            <CollectSectionCard>
                                <CollectSectionTitle>
                                    Customer
                                </CollectSectionTitle>
                                <Typography
                                    sx={{
                                        fontSize: "1rem",
                                        fontWeight: 700,
                                        mt: 1,
                                    }}
                                >
                                    {business?.name || "Unknown business"}
                                </Typography>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{ mt: 1.5, flexWrap: "wrap" }}
                                >
                                    {liveSub && (
                                        <Chip
                                            size="small"
                                            label={liveSub.tierLabel}
                                            sx={subscriptionTierChipSx(
                                                liveSub.tierLabel,
                                            )}
                                        />
                                    )}
                                    {liveSub && (
                                        <Chip
                                            size="small"
                                            label={`Priority: ${liveSub.supportPriority}`}
                                            sx={supportTierChipSx(
                                                liveSub.supportPriority,
                                            )}
                                        />
                                    )}
                                </Stack>
                                {ticket.subscriptionSnapshot && (
                                    <Typography
                                        sx={{
                                            fontSize: "0.72rem",
                                            color: "#9ca3af",
                                            mt: 1,
                                        }}
                                    >
                                        At submission:{" "}
                                        {ticket.subscriptionSnapshot
                                            .supportPriority || "low"}
                                    </Typography>
                                )}
                                <Typography
                                    sx={{
                                        fontSize: "0.8rem",
                                        color: "#6b7280",
                                        mt: 1,
                                    }}
                                >
                                    {ticket.submitterName} ·{" "}
                                    {ticket.submitterEmail}
                                </Typography>
                            </CollectSectionCard>

                            {/* Status control */}
                            <CollectSectionCard>
                                <CollectSectionTitle>
                                    Status
                                </CollectSectionTitle>
                                <Box sx={{ mt: 1.5, mb: 2 }}>
                                    <Chip
                                        label={supportStatusLabel(
                                            ticket.status as SupportTicketStatus,
                                        )}
                                        sx={supportStatusChipSx(
                                            ticket.status as SupportTicketStatus,
                                        )}
                                    />
                                </Box>
                                {allowedNext.length > 0 && (
                                    <Stack direction="row" spacing={1}>
                                        <TextField
                                            select
                                            label="Change to"
                                            value={nextStatus}
                                            onChange={(e) =>
                                                setNextStatus(e.target.value)
                                            }
                                            size="small"
                                            fullWidth
                                        >
                                            {allowedNext.map((s) => (
                                                <MenuItem key={s} value={s}>
                                                    {supportStatusLabel(s)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        <CollectSecondaryButton
                                            onClick={handleStatusChange}
                                            disabled={
                                                isSubmitting || !nextStatus
                                            }
                                            sx={{
                                                color: "#040d45",
                                                border: "1px solid rgba(4,13,69,0.2)",
                                                backgroundColor:
                                                    "rgba(4,13,69,0.04)",
                                                whiteSpace: "nowrap",
                                                "&:hover": {
                                                    backgroundColor:
                                                        "rgba(4,13,69,0.08)",
                                                },
                                            }}
                                        >
                                            Update
                                        </CollectSecondaryButton>
                                    </Stack>
                                )}
                            </CollectSectionCard>

                            {/* Priority + assignee */}
                            <CollectSectionCard>
                                <CollectSectionTitle>
                                    Triage
                                </CollectSectionTitle>
                                <TextField
                                    select
                                    label="User-facing priority"
                                    value={ticket.priority}
                                    onChange={(e) =>
                                        handlePriorityChange(
                                            e.target
                                                .value as SupportTicketPriority,
                                        )
                                    }
                                    size="small"
                                    fullWidth
                                    sx={{ mt: 1.5, mb: 2 }}
                                >
                                    {SUPPORT_PRIORITY_OPTIONS.map((o) => (
                                        <MenuItem key={o.value} value={o.value}>
                                            {o.label}
                                        </MenuItem>
                                    ))}
                                </TextField>

                                <Typography
                                    sx={{
                                        fontSize: "0.8rem",
                                        color: "#6b7280",
                                        mb: 1,
                                    }}
                                >
                                    Assigned to:{" "}
                                    <strong>
                                        {assignee ? assignee.name : "Unassigned"}
                                    </strong>
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                    <CollectSecondaryButton
                                        onClick={() => handleAssign(true)}
                                        disabled={isSubmitting}
                                        sx={{
                                            color: "#0f7a5b",
                                            border: "1px solid rgba(30,193,149,0.4)",
                                            backgroundColor:
                                                "rgba(30,193,149,0.08)",
                                            "&:hover": {
                                                backgroundColor:
                                                    "rgba(30,193,149,0.16)",
                                            },
                                        }}
                                    >
                                        Assign to me
                                    </CollectSecondaryButton>
                                    {assignee && (
                                        <CollectSecondaryButton
                                            onClick={() => handleAssign(false)}
                                            disabled={isSubmitting}
                                            sx={{
                                                color: "#6b7280",
                                                border: "1px solid rgba(107,114,128,0.3)",
                                                backgroundColor:
                                                    "rgba(107,114,128,0.06)",
                                                "&:hover": {
                                                    backgroundColor:
                                                        "rgba(107,114,128,0.12)",
                                                },
                                            }}
                                        >
                                            Unassign
                                        </CollectSecondaryButton>
                                    )}
                                </Stack>
                            </CollectSectionCard>

                            {/* Resolve */}
                            {ticket.status !== "resolved" &&
                                ticket.status !== "closed" && (
                                    <CollectSectionCard>
                                        <CollectSectionTitle>
                                            Resolve
                                        </CollectSectionTitle>
                                        <TextField
                                            label="Resolution note (emailed to user)"
                                            value={resolveNote}
                                            onChange={(e) =>
                                                setResolveNote(
                                                    e.target.value.slice(
                                                        0,
                                                        4000,
                                                    ),
                                                )
                                            }
                                            fullWidth
                                            multiline
                                            minRows={2}
                                            size="small"
                                            sx={{ mt: 1.5, mb: 1.5 }}
                                        />
                                        <CollectPrimaryButton
                                            startIcon={<CheckCircleOutline />}
                                            onClick={handleResolve}
                                            disabled={isSubmitting}
                                            fullWidth
                                        >
                                            Resolve ticket
                                        </CollectPrimaryButton>
                                    </CollectSectionCard>
                                )}
                        </Box>
                    </Box>
                )}
            </CollectContentArea>
        </CollectPageWrapper>
    );
};

const AdminMessage: React.FC<{
    author: string;
    body: string;
    at?: string;
    kind: "user" | "agent" | "internal";
}> = ({ author, body, at, kind }) => {
    const styles =
        kind === "internal"
            ? {
                bg: "rgba(245, 158, 11, 0.10)",
                border: "1px dashed rgba(245,158,11,0.5)",
                label: "#b45309",
            }
            : kind === "agent"
                ? {
                    bg: "rgba(30, 193, 149, 0.10)",
                    border: "1px solid rgba(30,193,149,0.25)",
                    label: "#0f7a5b",
                }
                : {
                    bg: "#f3f4f6",
                    border: "1px solid rgba(15,23,42,0.06)",
                    label: "#6b7280",
                };
    return (
        <Box sx={{ mb: 1.5 }}>
            <Box
                sx={{
                    backgroundColor: styles.bg,
                    border: styles.border,
                    borderRadius: "12px",
                    padding: "12px 14px",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        mb: 0.5,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            color: styles.label,
                        }}
                    >
                        {author}
                    </Typography>
                    {kind === "internal" && (
                        <Chip
                            size="small"
                            label="Internal note"
                            sx={{
                                height: 18,
                                fontSize: "0.6rem",
                                fontWeight: 700,
                                backgroundColor: "rgba(245,158,11,0.2)",
                                color: "#b45309",
                            }}
                        />
                    )}
                </Box>
                <Typography
                    sx={{
                        fontSize: "0.9rem",
                        color: "#1f2937",
                        whiteSpace: "pre-wrap",
                    }}
                >
                    {body}
                </Typography>
                {at && (
                    <Typography
                        sx={{ fontSize: "0.7rem", color: "#9ca3af", mt: 0.5 }}
                    >
                        {formatDateTime(at)}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default CollectSupportTicketAdmin;
